import React, {
  useMemo,
} from "react";
import { GoPencil } from "react-icons/go";
import {
  FiTrendingUp,
  FiTrendingDown,
  FiBarChart2,
  FiShoppingCart,
  FiSearch,
  FiTarget,
  FiZap,
  FiTag,
  FiActivity,
  FiPieChart,
  FiCheck,
  FiX,
} from "react-icons/fi";


// Pool of 10 distinct icon & color combinations for the icon badges
const INSIGHT_ICON_POOL = [
  { icon: FiTrendingUp, bg: "bg-emerald-100 border-emerald-200", text: "text-emerald-600" },
  { icon: FiTrendingDown, bg: "bg-rose-100 border-rose-200", text: "text-rose-600" },
  { icon: FiBarChart2, bg: "bg-blue-100 border-blue-200", text: "text-blue-600" },
  { icon: FiShoppingCart, bg: "bg-green-100 border-green-200", text: "text-green-600" },
  { icon: FiSearch, bg: "bg-indigo-100 border-indigo-200", text: "text-indigo-600" },
  { icon: FiTarget, bg: "bg-amber-100 border-amber-200", text: "text-amber-600" },
  { icon: FiZap, bg: "bg-yellow-100 border-yellow-200", text: "text-yellow-600" },
  { icon: FiTag, bg: "bg-purple-100 border-purple-200", text: "text-purple-600" },
  { icon: FiActivity, bg: "bg-cyan-100 border-cyan-200", text: "text-cyan-600" },
  { icon: FiPieChart, bg: "bg-pink-100 border-pink-200", text: "text-pink-600" },
];

// Sub-Component for Rendering Category Insights
export const CategoryInsights = ({
  insights,
  insightLoading,
  isEditing,
  editedInsights,
  onEditClick,
  onSave,
  onCancel,
  onInsightChange,
}) => {
  // Bold percentage values and numbers dynamically
  const highlightNumbers = (text) => {
    if (!text) return "";
    const regex = /(\$?\d+(?:[\.,]\d+)?%?)/g;
    return text.replace(regex, "<strong>$1</strong>");
  };

  // Shuffle icon choices per unique set of insights to ensure varied badges
  const assignedIcons = useMemo(() => {
    if (!insights || insights.length === 0) return [];

    const pool = [...INSIGHT_ICON_POOL];
    const shuffled = [];
    let seed = insights.length;

    for (let i = 0; i < pool.length; i++) {
      seed = (seed * 9301 + 49297) % 233280;
      const rndIndex = Math.floor((seed / 233280) * pool.length);
      shuffled.push(pool.splice(rndIndex, 1)[0]);
    }

    return insights.map(
      (_, index) => shuffled[index % INSIGHT_ICON_POOL.length],
    );
  }, [insights]);

  if (insightLoading) {
    return (
      <p className="text-sm text-blue-600 mt-4 px-2">Loading insights...</p>
    );
  }

  if (!insights || insights.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200 border-l-4 border-l-blue-600 rounded-lg p-5 shadow-sm relative mt-6 font-sans">
      {/* Floating Pencil Button (Top Right Offset to match Overall Insights) */}
      {!isEditing && (
        <button
          onClick={onEditClick}
          className="absolute -top-4 -right-4 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-10 print:hidden"
          title="Edit Insights"
        >
          <GoPencil size={24} />
        </button>
      )}

      {/* Header with Title and Save/Cancel Actions */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2 text-blue-600 font-semibold text-lg">
          <FiTrendingUp className="w-6 h-6 text-blue-600" />
          <span>Category Insights</span>
        </div>

        {/* Save / Cancel buttons when editing */}
        {isEditing && (
          <div className="flex items-center gap-2">
            <button
              onClick={onSave}
              className="flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded-md text-xs font-medium hover:bg-green-700 transition-colors shadow-sm"
            >
              <FiCheck size={14} /> Save
            </button>
            <button
              onClick={onCancel}
              className="flex items-center gap-1 bg-gray-500 text-white px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-600 transition-colors shadow-sm"
            >
              <FiX size={14} /> Cancel
            </button>
          </div>
        )}
      </div>

      {/* Stacked List Container (One by One) */}
      <div className="space-y-3">
        {(isEditing ? editedInsights : insights).map((insight, idx) => {
          const config = assignedIcons[idx] || INSIGHT_ICON_POOL[0];
          const IconComponent = config.icon;

          return (
            <div
              key={idx}
              className="flex items-start gap-4 p-4 rounded-lg shadow-sm hover:shadow transition-shadow"
            >
              {/* Left Side Icon Badge */}
              <div
                className={`flex-shrink-0 w-11 h-11 rounded-lg border flex items-center justify-center ${config.bg} ${config.text}`}
              >
                <IconComponent className="w-5 h-5" />
              </div>

              {/* Text Area */}
              <div className="flex-1 text-sm text-gray-700 leading-relaxed pt-0.5">
                {isEditing ? (
                  <div className="space-y-2">
                    {insight.category && (
                      <span className="font-semibold text-blue-900 block text-sm">
                        {insight.category}
                      </span>
                    )}
                    <textarea
                      value={insight.insight_text}
                      onChange={(e) => onInsightChange(idx, e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                    />
                  </div>
                ) : (
                  <p>
                    {insight.category && (
                      <strong className="font-bold text-[#2C3DA5] mr-1">
                        {insight.category} •
                      </strong>
                    )}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: highlightNumbers(insight.insight_text),
                      }}
                    />
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
